import React, { useState } from "react";
import "./FaqSection.css";

const faqData = [
  {
    question: "How do I issue a ticket in Sabre?",
    answer: [
      "To issue a ticket, ensure the booking is priced and a Price Quote (PQ) is stored. Use the entry below to issue the ticket with cash payment. For credit card payments, replace the fare form with the card details.",
      " W¥PQ1¥KP0¥FCASH",
      "W¥PQ1¥KP0¥F*CA(CARD NUMBER)/EXPIRY DATE",
    ],
    note: "Tip: Always verify that contact information (CTCM/CTCE) is added to the booking before ticketing to avoid errors.",
  },
  {
    question: "How do I price a booking and store a Price Quote (PQ)?",
    answer: [
      "Use the WPNCB entry to price a booking. To compare fares across different airlines or classes, store multiple Price Quotes (PQs). To delete an expired PQ and reprice, use the entries below. ",
      "WPNCB¥RQ  (store PQ)",
      " WPNCB¥A(AIRLINE)¥RQ  (price specific airline) ",
      " WPNCB¥S1 / WPNCB¥S2  (price different classes) ",
      " PQD(PQ NUMBER)  (delete expired PQ)",
      "WPNI  (check if lower fare exists)",
    ],
    note: "Tip: If you receive a 'NO COMBINABLE FARES' error, escalate to Level 2 Support — this may affect all GDS platforms.",
  },
  {
    question: "How do I process a refund or reissue a ticket?",
    answer: [
      "For automated refunds and reissues, use the entries below. For manual refunds or reissues (e.g., partial refund or no ADC reissue), use the manual entries. Always check fare rules before processing.",
      "WFRA(TICKET NUMBER)/REF  (automated refund)",
      " WFRF(TICKET NUMBER)  (calculate fare difference & penalty) ",
      "WFR(TICKET NUMBER) – MANUAL REISSUE ",
      "WFR(TICKET NUMBER) – MANUAL REFUND partial",
      "WTRX(TICKET NUMBER)  (cancel a refund) ",
    ],
    note: "Tip: If the ticket was issued in another GDS, contact the originating GDS or airline directly. Waiver codes must be obtained from the airline for out-of-policy refunds",
  },
  {
    question:
      "I cannot log in to Sabre / the application is not working. What should I do?",
    answer: [
      "Most login and application issues can be resolved quickly by switching to Sabre Red Web instead of the desktop application. If MFA (Multi-Factor Authentication) is failing, a reset is required. ",
      " Use Sabre Red Web: https://www.mysabre.com",
      "For MFA issues: contact the TNS helpdesk for a reset ",
      "For 'APPLICATION IN USE' errors: reinstall the Sabre application ",
    ],
    note: "Tip: Antivirus or firewall software can block the Sabre connection. If web access works but the application does not, flag this to your IT team. ",
  },
  {
    question: "How do I create a booking and check flight availability?",
    answer: [
      "Use the availability entry to search for flights, then create a PNR. To retrieve an existing booking, use the PNR locator. For past-dated bookings, use the historical retrieval entry. ",
      "  1(DATE)(CITY PAIR)¥(AIRLINE)  (availability search)",
      "*PNR  (display booking)",
      "*XXXX-PNR-P  (retrieve past-dated PNR) ",
      "D1.1-6P-F-6P-ER  (divide/split a PNR)",
    ],
    note: "Tip: If a segment returns UC status, the booking class is unavailable — try an alternative class or contact the airline.",
  },
  {
    question: "How do I reset a password or create a new EPR / PCC?",
    answer: [
      "Password resets, new EPR creation, and PCC setup are all handled by the TNS helpdesk. Submit your request with the agent name, PCC, and reason. For EPRs requiring restricted permissions (no ticketing rights), specify this clearly in your request. ",
      "Contact: TNS Helpdesk  |  hr@tnsglobal.co",
      "Specify: Agent name · PCC · Request type (reset / new EPR / new PCC) ",
    ],
    note: "Tip: If the PCC has been deleted, the reset cannot be completed automatically — escalate to Level 2 Support for investigation.",
  },
  {
    question: "How do I add passenger passport (APIS) details to a booking?",
    answer: [
      "APIS details are required by many airlines and must be added before ticketing. Use the entry below to add passport information. To verify existing APIS/SSR details, use *PD3. ",
      "3DOCS(SEG NO)/(DOC TYPE)/(ISSUE COUNTRY)/(DOC",
      "NUMBER)/(NATIONALITY)/(DOB:DDMMMYYYY)/(GENDER)/(EXPIRY:DDMMMYYYY)/(LAST",
      "NAME)/(FIRST NAME)/(MIDDLE NAME)-(NAME NUMBER)",
      "  *PD3  (verify passport SSR details) ",
    ],
    note: "Tip: APIS must be added before ticketing for routes requiring it. Failure to do so will result in a ticket issuance error.",
  },
  {
    question: "How do I update a booking status or confirm a schedule change?",
    answer: [
      "When segments change to TK or UN status following a schedule change, use EWR to update them to HK. Always confirm the change with the passenger before updating. ",
      "EWR  (confirm KK/TK/UN segments to HK)",
      "WPNCB  (reprice after status update)",
    ],
    note: "Tip:  If a passenger misses a connection due to an unactioned schedule change, contact the airline immediately for rerouting.",
  },
  {
    question: "How do I add seat assignments or baggage as air extras?",
    answer: [
      "Seat assignments and baggage extras are managed through the Electronic Miscellaneous Document (EMD) workflow in Sabre. Use the Workflows tool to issue the EMD. For seat assignment support, use Air Extra in the booking flow. ",
      " Issue EMD via Sabre Workflows (Air Extras menu)",
      "For bulkroom / legroom seats: contact the airline directly if EMD is not available in Sabre",
    ],
    note: "Tip: Not all airlines support Sabre air extras. If the option is unavailable, advise the client to contact the airline directly.",
  },
  {
    question: "How do I void a ticket issued in error?",
    answer: [
      "Tickets can only be voided on the same calendar day of issuance. Use the entry below twice (once to initiate, once to confirm). After the void window closes, contact the airline for a manual refund. ",
      " WV(TICKET LINE NUMBER)  — enter twice to confirm",
      " WV(TICKET LINE NUMBER)(TICKET LINE NUMBER)  — void duplicate tickets",
    ],
    note: "Tip: Void must be completed on the same business day. After midnight local time, the ticket moves into refund territory and standard fare rules apply.",
  },
];

export default function FaqSection() {
  const [active, setActive] = useState(0);

  return (
    <section className="faq-section section-padding">
      <div className="faq-pattern-left"></div>
      <div className="faq-pattern-right"></div>

      <div className="faq-container">
        <h2 className="heading-title faq-heading">
          <span className="title-blue">Got Questions?</span> We've Got
          <br />
          Answers!
        </h2>

        <p className="sub-text faq-subtext">
          Discover how we help travel agencies, airlines, and travel businesses
          leverage world-class technology with trusted local expertise.
        </p>

        <div className="faq-list">
          {faqData.map((item, index) => (
            <div
              key={index}
              className={`faq-item ${active === index ? "active" : ""}`}
            >
              <button
                className="faq-question"
                onClick={() => setActive(active === index ? -1 : index)}
              >
                <span>{item.question}</span>

                <span className="faq-icon">{active === index ? "−" : "+"}</span>
              </button>

              {active === index && (
                <div className="faq-answer">
                  {Array.isArray(item.answer) ? (
                    item.answer.map((line, idx) => <p key={idx}>{line}</p>)
                  ) : (
                    <p>{item.answer}</p>
                  )}

                  {item.note && <p className="faq-note">{item.note}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
